import { useSyncExternalStore } from 'react'
import { b64u, seal, unb64u } from '@stoaria/vault-core'
import {
  buildRecoveryFile,
  privateKeyFromRecoveryFile,
} from './vault-recovery-file.ts'
import {
  forget,
  countRecordsForRoot,
  hasStoredKey,
  loadStoredKey,
  store,
} from './vault-key-store.ts'
import {
  adoptImportedKey,
  adoptKeyHandle,
  getSession,
  isUnlocked,
  lock,
  noteActivity,
  subscribeUnlocked,
  withPrivateKey,
} from './vault-key-session.ts'
import { isIdleWarningActive, subscribeIdleWarning } from './vault-idle-lock.ts'
import { openValue, sealValueForTenant } from './vault-value-crypto.ts'
import type { SealedValue } from './vault-value-crypto.ts'
import {
  cancelCeremony,
  getCeremony,
  runRawKeyCeremony,
  subscribeCeremony,
  submitCeremony,
} from './vault-raw-key-ceremony.ts'
import type { CeremonyRequest } from './vault-raw-key-ceremony.ts'

// The browser-side holder of the tenant KEK's PRIVATE half — the public
// face of it, anyway; the state machine is `vault-key-session.ts` and the
// thing on disk is `vault-key-store.ts`.
//
// This file used to keep a `Uint8Array` at module scope under a comment
// insisting it never be persisted. ADR 0009 replaced that, and the reason
// is worth repeating where the next reader will find it: the memory-only
// rule was not being complied with, it was being routed around. A reload
// every half hour meant the recovery file ended up somewhere convenient
// to paste from, which is strictly worse than any storage scheme we could
// have designed.
//
// What replaced it is as small as the problem allows:
//
//   - In memory, a non-extractable `CryptoKey`. Usable, unreadable.
//   - At rest, THE SAME HANDLE, structure-cloned into IndexedDB. Nothing
//     is wrapped, there is no second factor, and no plaintext PKCS8 ever
//     passes through the heap on the stored-unlock path.
//   - Raw bytes only inside `withRawPrivateKey`, from a recovery file the
//     operator pastes for that one operation, zeroed in a `finally`.
//
// An earlier attempt on this branch wrapped the key under a WebAuthn PRF
// with a passphrase fallback. It is gone, and the ADR records why: too
// much machinery for a single credential inside a product, and the
// cross-device benefit that justified it was illusory while the blob
// itself lived in per-browser IndexedDB.
//
// The module is a thin top of three: `vault-key-session.ts` holds the
// live key, `vault-key-store.ts` the key at rest, `vault-value-crypto.ts`
// the per-value envelope. What they export is re-exported here rather than
// redefined, because every call site in the app imports from this path and
// none of them should have to care that the state moved one file over.
export {
  isUnlocked,
  lock,
  noteActivity,
  openValue,
  sealValueForTenant,
  subscribeUnlocked,
  withPrivateKey,
}
export type { SealedValue }

// The store's error type, re-exported so a caller can switch on
// `.reason` without reaching past this module — "there is no IndexedDB
// here" and "the key on this disk is not this tenant's" are different
// things to do and different things to say.
export { VaultKeyStoreError } from './vault-key-store.ts'
export type { VaultKeyStoreReason } from './vault-key-store.ts'

// The ceremony's vocabulary, for the prompt component and for callers
// that have to tell "the operator said no" from "it went wrong".
export { cancelCeremony, submitCeremony }
export {
  CeremonyBusyError,
  CeremonyCancelledError,
} from './vault-raw-key-ceremony.ts'
export type { CeremonyRequest }

export interface UnlockOptions {
  /** Vault root this key belongs to. Required to persist. */
  rootId?: string
  /** Keep the key on this device, so the next page load costs nothing at
   * all. Omit for memory-only — still fully supported, and the right
   * answer on a shared machine. */
  remember?: boolean
}

/** What `unlock` reports back. The unlock itself either happened or
 * threw; this is only about whether the key was also kept on the
 * device. */
export interface UnlockOutcome {
  remembered: boolean
  /** Why it was not remembered, when `remember` was asked for and did not
   * happen — a browser with no IndexedDB, a locked-down profile, a
   * private window. `null` when nothing was asked for or nothing went
   * wrong. */
  rememberError: Error | null
}

/**
 * Parses a pasted recovery file, verifies its public half against the
 * server's known tenant public key, imports the private half as a
 * non-extractable handle, and zeroes the bytes.
 *
 * The public-key check is unchanged and not negotiable — see
 * `vault-recovery-file.ts` for what it is actually protecting against.
 *
 * Rejects (never partially unlocks) on a parse failure or a mismatch. It
 * does NOT reject when `remember` fails: a valid recovery file plus a
 * browser that will not store anything must not cost the operator their
 * session — that friction is the whole reason ADR 0009 exists. The unlock
 * stands, memory-only, and the outcome says so.
 *
 * The adopt happens BEFORE the store, and the order matters: the handle
 * written to IndexedDB is then provably the same object this tab is
 * using, rather than a second import of the same bytes that merely ought
 * to agree with it.
 */
export async function unlock(
  recoveryFileText: string,
  serverTenantPubkey: string,
  options: UnlockOptions = {},
): Promise<UnlockOutcome> {
  const { rootId, remember = false } = options
  const pkcs8 = privateKeyFromRecoveryFile(recoveryFileText, serverTenantPubkey)
  try {
    const key = await adoptImportedKey(
      pkcs8,
      serverTenantPubkey,
      unb64u(serverTenantPubkey),
      rootId ?? null,
    )
    if (!remember || rootId === undefined || rootId === '')
      return { remembered: false, rememberError: null }
    return await tryRemember(rootId, serverTenantPubkey, key)
  } finally {
    // Every path: a failed import, a refused store, a thrown parse.
    // None of them may leave the private key in a live buffer.
    pkcs8.fill(0)
  }
}

/** Writes the handle to the store and turns a failure into an outcome
 * rather than an exception.
 *
 * Everything is demoted, not just a modelled subset, and that is a
 * deliberate change from the pre-ADR-0009 version. The store's contract
 * is four functions and no error type, so there is nothing here to switch
 * on — and the alternative to demoting is failing an unlock that already
 * succeeded. Nothing is swallowed: the error travels out in
 * `rememberError` and `VaultUnlockBanner` puts it on screen, because the
 * operator asked for something that did not happen and would otherwise
 * discover it on the next reload. */
async function tryRemember(
  rootId: string,
  tenantPubkey: string,
  key: CryptoKey,
): Promise<UnlockOutcome> {
  try {
    await store({ rootId, tenantPubkey, key })
    return { remembered: true, rememberError: null }
  } catch (err) {
    return {
      remembered: false,
      rememberError:
        err instanceof Error
          ? err
          : new Error('this browser would not store the tenant key', {
              cause: err,
            }),
    }
  }
}

/** Whether this device has a stored key for exactly this root AND this
 * tenant public key. Both halves matter: a record left behind by a
 * rotated-away key must not match. False, never a throw, where IndexedDB
 * is absent. */
export function hasStoredUnlock(
  rootId: string,
  serverTenantPubkey: string,
): Promise<boolean> {
  return hasStoredKey(rootId, serverTenantPubkey)
}

/** Whether this device is holding ANY key for this root, including one
 * left behind by a rotated-away tenant key.
 *
 * This is the probe "Forget this device" reports on, because
 * `forgetStoredKey` deletes by root. `hasStoredUnlock` is the narrower
 * question the UNLOCK path asks, and using it for the forget readout
 * stranded exactly the record an operator most wants gone: a browser
 * holding only a pre-rotation record read "Not remembered" and offered
 * no button. */
export async function hasAnyStoredUnlock(rootId: string): Promise<boolean> {
  return (await countRecordsForRoot(rootId)) > 0
}

/**
 * The zero-gesture unlock: adopt the handle this browser already has.
 *
 * Returns `false` when there is nothing stored for this root and key, so
 * a page-load effect can call it unconditionally and fall through to the
 * paste banner. Throws when a record IS there and will not load — the
 * store self-tests what it read, so that means the stored handle does not
 * match this tenant key, and "nothing is stored" and "what is stored is
 * wrong" are different things to put in front of an operator.
 */
export async function tryStoredUnlock(
  rootId: string,
  serverTenantPubkey: string,
): Promise<boolean> {
  if (!(await hasStoredKey(rootId, serverTenantPubkey))) return false
  const key = await loadStoredKey(rootId, serverTenantPubkey)
  adoptKeyHandle(key, serverTenantPubkey, unb64u(serverTenantPubkey), rootId)
  return true
}

/** Drops the stored key and locks. "Forget this device" that left the tab
 * holding a usable key would be the same kind of theatre as a Lock button
 * that only locks one tab. */
export async function forgetStoredKey(rootId: string): Promise<void> {
  await forget(rootId)
  lock()
}

/**
 * Throws away a stored record that would not load, without locking.
 *
 * Separate from `forgetStoredKey` because of the `lock()`: a record that
 * failed its self-test is unusable, so there is nothing in THIS tab to
 * lock — but `lock()` is a cross-tab broadcast, and taking down a sibling
 * tab's perfectly good session because this one found a bad record on
 * disk would be a self-inflicted outage.
 *
 * Discarding rather than keeping is the right call: a record that cannot
 * be loaded is not a backup of anything, and leaving it means every
 * subsequent reload retries it and fails the same way. One paste puts a
 * good one back.
 */
export function discardStoredKey(rootId: string): Promise<void> {
  return forget(rootId)
}

/** Rotation's device-storage half, which lives in its own module
 * because everything in it runs after the rotation has already committed
 * and therefore must never throw. Re-exported so `VaultTenantKey` keeps
 * importing from one path. */
export { replacePrivateKey } from './vault-key-rotation.ts'

/** Reactive replacement for a bare `isUnlocked()` call in a component
 * body. Re-renders the caller on every unlock/lock — including one
 * triggered by a sibling component, or by another tab — instead of
 * reading a stale snapshot until the next unrelated re-render. */
export function useIsUnlocked(): boolean {
  return useSyncExternalStore(subscribeUnlocked, isUnlocked, isUnlocked)
}

/** True for the last 60 seconds before the idle timeout fires, for the
 * component that renders the warning and its "stay unlocked" button. */
export function useIdleWarning(): boolean {
  return useSyncExternalStore(
    subscribeIdleWarning,
    isIdleWarningActive,
    isIdleWarningActive,
  )
}

/** The recovery-file prompt currently on screen, or `null` — what
 * `VaultCeremonyPrompt` renders. */
export function useRawKeyCeremony(): CeremonyRequest | null {
  return useSyncExternalStore(subscribeCeremony, getCeremony, getCeremony)
}

/**
 * Runs `fn` with the tenant private key as PKCS8 BYTES, behind a fresh
 * recovery-file paste, and zeroes them afterwards on every path.
 *
 * Three operations may call this, all for the same reason: the key bytes
 * ARE their payload, which a non-extractable handle cannot supply.
 * `reason` is shown to the operator (`useRawKeyCeremony`), so "why is
 * this asking for my recovery file" always has an answer.
 *
 * Delegates to `runRawKeyCeremony`, the single materialisation point;
 * this wrapper only adds the lock check and the session lookup. Nothing
 * else in the app may import that module.
 *
 * The lock check is not ceremony for its own sake even though the paste
 * would supply the bytes regardless: these operations act on behalf of an
 * unlocked session (they name a daemon of this root, they write this
 * tenant's file), and letting them run from a locked page would make
 * "locked" mean nothing.
 */
export function withRawPrivateKey<T>(
  reason: string,
  fn: (pkcs8: Uint8Array) => Promise<T> | T,
): Promise<T> {
  const session = getSession()
  if (session === null)
    return Promise.reject(
      new Error('tenant key is locked — paste the recovery file first'),
    )
  noteActivity()
  return runRawKeyCeremony(session, reason, fn)
}

/** Seals the tenant PRIVATE key to a daemon's public key, for delivery on
 * approval — the daemon opens it with its own private key to recover the
 * KEK.
 *
 * One of the operations that cannot run on a handle: the key bytes ARE
 * the plaintext. It therefore costs a recovery-file paste.
 *
 * `daemonLabel` is the host the key is about to be handed to, and it is
 * worth threading through for one reason: the only place an operator can
 * learn WHY they are being asked for the recovery file is the prompt this
 * reason feeds. "Deliver the tenant key to a daemon" answers that
 * question far less well than naming the host that is about to hold the
 * key, and the whole point of a confirmation is that the operator can
 * tell a legitimate one from an unexpected one. */
export function sealKekToDaemon(
  daemonPubkey: string,
  daemonLabel?: string,
): Promise<string> {
  const reason =
    daemonLabel === undefined || daemonLabel === ''
      ? 'Deliver the tenant key to a daemon'
      : `Deliver the tenant key to the daemon ${daemonLabel}`
  return withRawPrivateKey(reason, async (pkcs8) =>
    b64u(await seal(pkcs8, unb64u(daemonPubkey))),
  )
}

/**
 * Re-issues this tenant's recovery file from an unlocked session.
 *
 * ADR 0009 gave up cross-device unlock, and this is what makes that
 * tolerable: before it, the file existed only at the instant the tenant
 * key was created, so an operator who had lost track of theirs had no way
 * to produce another and arming a second machine was an archaeology
 * exercise.
 *
 * BE CLEAR ABOUT WHAT THIS IS NOT. It cannot conjure the file out of the
 * handle — the handle is non-extractable, which is the entire point — so
 * it goes through the same paste ceremony as every other raw-bytes
 * operation. What it gives back is a verified, canonically formatted,
 * correctly named file: the paste is checked against the live tenant
 * public key, so the operator learns whether the material they have is
 * actually this tenant's CURRENT key before they rely on it. An operator
 * with no copy at all is not rescued by this, and the UI must not imply
 * otherwise.
 *
 * SECURITY: returns the tenant private key as a string, which is the one
 * thing the rest of this module exists to avoid. It is unavoidable for a
 * file the operator downloads — `VaultRecoveryGate` is the only intended
 * consumer — and the string must not be logged, stored, or sent anywhere.
 */
export function exportRecoveryFile(): Promise<string> {
  const session = getSession()
  if (session === null)
    return Promise.reject(
      new Error('tenant key is locked — unlock before exporting'),
    )
  return withRawPrivateKey(
    'Download this tenant key’s recovery file',
    (pkcs8) => buildRecoveryFile(pkcs8, session.recipient.publicKey),
  )
}
